/**
 * PageEditorModal Component
 *
 * @deprecated This modal is deprecated. Use the route-based PageEditorPage instead:
 * - Create new page: navigate('/pages/new') or navigate('/pages/new?template=<type>')
 * - Edit existing page: navigate(`/pages/${id}/edit`)
 *
 * This modal is kept for backwards compatibility but is no longer rendered in the app.
 * See: frontend/src/client/pages/page-editor.tsx for the new implementation.
 *
 * Original description:
 * Wizard-based modal for creating and editing landing pages with a step-by-step flow.
 * Supports multiple templates (product, link-in-bio, event) with template-specific content.
 * Includes live preview, optional short link/QR code generation, and publishing.
 */

import { useState, useEffect } from 'react';
import { useForm, useFormContext, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Loader2,
  FileText,
  Layers,
  Palette,
  Type,
  Image,
  MousePointerClick,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Globe,
  ExternalLink,
  Upload,
  Trash2,
  Link2,
  Calendar,
  MapPin,
  Clock,
  Users,
  HelpCircle,
  ImageIcon,
  CheckCircle2,
  Monitor,
  Smartphone,
  QrCode,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { useUIStore } from '@/client/stores/ui-store';
import { apiFetch } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { useIsMobile } from '@/client/hooks/use-mobile';
import { Button } from '@/client/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import {
  LinksEditor,
  SocialsEditor,
  GalleryEditor,
  ScheduleEditor,
  FAQEditor,
  FormSectionEditor,
  type LinkItem,
  type SocialItem,
  type GalleryImage,
  type ScheduleItem,
  type FAQItem,
  type FormSectionValue,
} from '@/client/components/page-editor';
import {
  SplitScreenWizardModal,
  WizardStep,
  type WizardStepConfig,
} from '@/client/components/wizard';
import { useKeyboard } from '@/client/hooks/use-keyboard';
import { useDebounce } from '@/client/hooks/use-debounce';
import { ColorPicker } from '@/client/components/ui/color-picker';
import { ThemePicker } from '@/client/components/ui/theme-picker';
import { TypographyControls } from '@/client/components/ui/typography-controls';
import { VisualEffectsControls } from '@/client/components/ui/visual-effects-controls';
import type { PageTheme } from '@/client/lib/themes';

// =============================================================================
// Types
// =============================================================================

interface ProjectOption {
  id: string;
  name: string;
  color: string;
}

type TemplateType = 'product-launch' | 'link-in-bio' | 'event';

// Wizard steps configuration - streamlined to reduce friction
const getWizardSteps = (): WizardStepConfig[] => [
  { id: 'basics', label: 'Basics', icon: FileText },
  { id: 'content', label: 'Content', icon: Type },
  { id: 'style', label: 'Style', icon: Palette, optional: true },
  { id: 'publish', label: 'Publish', icon: CheckCircle2 },
];

// =============================================================================
// Validation Schemas (per template)
// =============================================================================

// Base schema shared by all templates
const baseSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(100)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers, and hyphens only'),
  projectId: z.string().min(1, 'Project is required'),
  styleOptions: z.object({
    backgroundColor: z.string().optional(),
    accentColor: z.string().optional(),
    textColor: z.string().optional(),
    headingColor: z.string().optional(),
    fontFamily: z
      .enum([
        'inter',
        'poppins',
        'merriweather',
        'system',
        'playfair',
        'roboto',
        'montserrat',
        'lora',
      ])
      .optional(),
    maxWidth: z.enum(['narrow', 'medium', 'wide']).optional(),
    padding: z.enum(['compact', 'normal', 'spacious']).optional(),
    // Typography settings
    headingSize: z.number().min(1.5).max(4).optional(),
    bodySize: z.number().min(0.75).max(1.5).optional(),
    lineHeight: z.number().min(1).max(2.5).optional(),
    letterSpacing: z.number().min(-0.05).max(0.2).optional(),
    headingWeight: z.number().min(300).max(900).optional(),
    bodyWeight: z.number().min(300).max(900).optional(),
    // Visual effects settings
    borderRadius: z.number().min(0).max(32).optional(),
    shadowX: z.number().min(-20).max(20).optional(),
    shadowY: z.number().min(-20).max(20).optional(),
    shadowBlur: z.number().min(0).max(50).optional(),
    shadowSpread: z.number().min(-20).max(20).optional(),
    shadowColor: z.string().optional(),
    shadowEnabled: z.boolean().optional(),
    gradientEnabled: z.boolean().optional(),
    gradientType: z.enum(['linear', 'radial']).optional(),
    gradientAngle: z.number().min(0).max(360).optional(),
    gradientStops: z
      .array(
        z.object({
          color: z.string(),
          position: z.number().min(0).max(100),
        })
      )
      .optional(),
    opacity: z.number().min(0).max(100).optional(),
  }),
  // Links & QR options
  createShortLink: z.boolean().optional(),
  createQRCode: z.boolean().optional(),
});

// Form section schema (shared across all templates)
const formSectionSchema = z
  .object({
    formId: z.string(),
    mode: z.enum(['inline', 'button']),
    sectionTitle: z.string().max(200).optional(),
    buttonText: z.string().max(50).optional(),
    buttonStyle: z.enum(['primary', 'secondary', 'outline']).optional(),
    hideTitle: z.boolean().optional(),
    hideDescription: z.boolean().optional(),
  })
  .nullable()
  .optional();

// Product template schema
const productContentSchema = z.object({
  hero: z.object({
    headline: z.string().min(1, 'Headline is required').max(200),
    subheadline: z.string().max(500).optional(),
    heroImageUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
    heroAlignment: z.enum(['left', 'center', 'right']),
  }),
  cta: z.object({
    buttonText: z.string().min(1, 'Button text is required').max(50),
    buttonUrl: z.string().max(500),
    buttonStyle: z.enum(['primary', 'secondary', 'outline']),
  }),
  formSection: formSectionSchema,
});

// Link-in-Bio template schema
const linkInBioContentSchema = z.object({
  profile: z.object({
    name: z.string().min(1, 'Name is required').max(100),
    tagline: z.string().max(200).optional(),
    avatarUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  }),
  links: z.array(
    z.object({
      id: z.string(),
      label: z.string().max(100),
      url: z.string().max(500),
      icon: z.string().max(4).optional(),
    })
  ),
  socials: z.array(
    z.object({
      id: z.string(),
      platform: z.enum([
        'twitter',
        'instagram',
        'linkedin',
        'youtube',
        'tiktok',
        'github',
        'facebook',
      ]),
      url: z.string().max(500),
    })
  ),
  formSection: formSectionSchema,
});

// Event template schema
const eventContentSchema = z.object({
  hero: z.object({
    title: z.string().min(1, 'Title is required').max(200),
    subtitle: z.string().max(500).optional(),
    date: z.string().max(100).optional(),
    time: z.string().max(100).optional(),
    location: z.string().max(200).optional(),
    locationUrl: z.string().url().optional().or(z.literal('')),
    backgroundImage: z.string().url().optional().or(z.literal('')),
  }),
  gallery: z.array(
    z.object({
      id: z.string(),
      url: z.string(),
      alt: z.string().optional(),
    })
  ),
  schedule: z.array(
    z.object({
      id: z.string(),
      time: z.string().max(50),
      title: z.string().max(200),
      description: z.string().max(500).optional(),
    })
  ),
  faq: z.array(
    z.object({
      id: z.string(),
      question: z.string().max(500),
      answer: z.string().max(2000),
    })
  ),
  rsvp: z
    .object({
      enabled: z.boolean().optional(),
      buttonText: z.string().max(50).optional(),
      buttonUrl: z.string().max(500).optional(),
    })
    .optional(),
  formSection: formSectionSchema,
});

// Combined schema using discriminated approach
const productSchema = baseSchema.extend({ content: productContentSchema });
const linkInBioSchema = baseSchema.extend({ content: linkInBioContentSchema });
const eventSchema = baseSchema.extend({ content: eventContentSchema });

type ProductInput = z.infer<typeof productSchema>;
type LinkInBioInput = z.infer<typeof linkInBioSchema>;
type EventInput = z.infer<typeof eventSchema>;

// Union type for form data
type PageEditorInput = ProductInput | LinkInBioInput | EventInput;

// =============================================================================
// Default Values
// =============================================================================

const productDefaults: ProductInput = {
  name: '',
  description: '',
  slug: '',
  projectId: '',
  content: {
    hero: {
      headline: 'Welcome',
      subheadline: '',
      heroImageUrl: '',
      heroAlignment: 'center',
    },
    cta: {
      buttonText: 'Get Started',
      buttonUrl: '#',
      buttonStyle: 'primary',
    },
    formSection: null,
  },
  styleOptions: {
    backgroundColor: '#ffffff',
    accentColor: '#10b981',
    textColor: '#1f2937',
    headingColor: '#111827',
    fontFamily: 'inter',
    maxWidth: 'medium',
    padding: 'normal',
    headingSize: 2.5,
    bodySize: 1,
    lineHeight: 1.6,
    letterSpacing: 0,
    headingWeight: 700,
    bodyWeight: 400,
  },
  createShortLink: true,
  createQRCode: true,
};

const linkInBioDefaults: LinkInBioInput = {
  name: '',
  description: '',
  slug: '',
  projectId: '',
  content: {
    profile: {
      name: '',
      tagline: '',
      avatarUrl: '',
    },
    links: [],
    socials: [],
    formSection: null,
  },
  styleOptions: {
    backgroundColor: '#ffffff',
    accentColor: '#10b981',
    textColor: '#1f2937',
    headingColor: '#111827',
    fontFamily: 'inter',
    maxWidth: 'narrow',
    padding: 'normal',
    headingSize: 2.5,
    bodySize: 1,
    lineHeight: 1.6,
    letterSpacing: 0,
    headingWeight: 700,
    bodyWeight: 400,
  },
  createShortLink: true,
  createQRCode: true,
};

const eventDefaults: EventInput = {
  name: '',
  description: '',
  slug: '',
  projectId: '',
  content: {
    hero: {
      title: "You're Invited!",
      subtitle: '',
      date: '',
      time: '',
      location: '',
      locationUrl: '',
      backgroundImage: '',
    },
    gallery: [],
    schedule: [],
    faq: [],
    rsvp: {
      enabled: false,
      buttonText: 'RSVP Now',
      buttonUrl: '',
    },
    formSection: null,
  },
  styleOptions: {
    backgroundColor: '#ffffff',
    accentColor: '#10b981',
    textColor: '#1f2937',
    headingColor: '#111827',
    fontFamily: 'poppins',
    maxWidth: 'medium',
    padding: 'normal',
    headingSize: 2.5,
    bodySize: 1,
    lineHeight: 1.6,
    letterSpacing: 0,
    headingWeight: 700,
    bodyWeight: 400,
  },
  createShortLink: true,
  createQRCode: true,
};

function getDefaultsForTemplate(templateType: TemplateType): PageEditorInput {
  switch (templateType) {
    case 'link-in-bio':
      return linkInBioDefaults;
    case 'event':
      return eventDefaults;
    default:
      return productDefaults;
  }
}

function getSchemaForTemplate(templateType: TemplateType) {
  switch (templateType) {
    case 'link-in-bio':
      return linkInBioSchema;
    case 'event':
      return eventSchema;
    default:
      return productSchema;
  }
}

// =============================================================================
// Template Labels
// =============================================================================

const TEMPLATE_LABELS: Record<TemplateType, string> = {
  'product-launch': 'Product Launch',
  'link-in-bio': 'Link in Bio',
  event: 'Event',
};

// =============================================================================
// Preview HTML Generator
// =============================================================================

/**
 * Generate preview HTML from form data
 * Creates a simple but realistic preview of the landing page
 */
function generatePreviewHtml(formData: PageEditorInput, templateType: TemplateType): string {
  const styles = formData.styleOptions || {};
  const bgColor = styles.backgroundColor || '#ffffff';
  const accentColor = styles.accentColor || '#10b981';
  const textColor = styles.textColor || '#1f2937';
  const headingColor = styles.headingColor || '#111827';
  const fontFamily = styles.fontFamily || 'inter';

  const fontStack =
    {
      inter: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      poppins: 'Poppins, -apple-system, sans-serif',
      merriweather: 'Merriweather, Georgia, serif',
      playfair: 'Playfair Display, Georgia, serif',
      roboto: 'Roboto, -apple-system, sans-serif',
      montserrat: 'Montserrat, -apple-system, sans-serif',
      lora: 'Lora, Georgia, serif',
      system: 'system-ui, sans-serif',
    }[fontFamily] || '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

  // Typography settings
  const headingSize = styles.headingSize || 2.5;
  const bodySize = styles.bodySize || 1;
  const lineHeight = styles.lineHeight || 1.6;
  const letterSpacing = styles.letterSpacing || 0;
  const headingWeight = styles.headingWeight || 700;
  const bodyWeight = styles.bodyWeight || 400;

  // Visual effects settings
  const borderRadius = styles.borderRadius ?? 8;
  const shadowX = styles.shadowX ?? 0;
  const shadowY = styles.shadowY ?? 4;
  const shadowBlur = styles.shadowBlur ?? 10;
  const shadowSpread = styles.shadowSpread ?? 0;
  const shadowColor = styles.shadowColor ?? '#00000040';
  const shadowEnabled = styles.shadowEnabled ?? false;
  const gradientEnabled = styles.gradientEnabled ?? false;
  const gradientType = styles.gradientType ?? 'linear';
  const gradientAngle = styles.gradientAngle ?? 135;
  const gradientStops = styles.gradientStops ?? [
    { color: '#3b82f6', position: 0 },
    { color: '#8b5cf6', position: 100 },
  ];
  const opacity = styles.opacity ?? 100;

  // Generate box shadow
  const boxShadow = shadowEnabled
    ? `${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowSpread}px ${shadowColor}`
    : 'none';

  // Generate gradient
  let backgroundStyle = bgColor;
  if (gradientEnabled) {
    const stops = gradientStops.map((stop) => `${stop.color} ${stop.position}%`).join(', ');

    if (gradientType === 'linear') {
      backgroundStyle = `linear-gradient(${gradientAngle}deg, ${stops})`;
    } else {
      backgroundStyle = `radial-gradient(circle, ${stops})`;
    }
  }

  // Base HTML template
  const baseHtml = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${formData.name || 'Preview'}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: ${fontStack};
          background: ${backgroundStyle};
          color: ${textColor};
          font-size: ${bodySize}rem;
          line-height: ${lineHeight};
          letter-spacing: ${letterSpacing}em;
          font-weight: ${bodyWeight};
          opacity: ${opacity / 100};
        }
        .container {
          max-width: ${styles.maxWidth === 'narrow' ? '600px' : styles.maxWidth === 'wide' ? '1200px' : '900px'};
          margin: 0 auto;
          padding: ${styles.padding === 'compact' ? '1rem' : styles.padding === 'spacious' ? '3rem' : '2rem'};
        }
        h1, h2, h3 {
          color: ${headingColor};
          font-weight: ${headingWeight};
          line-height: 1.2;
        }
        h1 { font-size: ${headingSize}rem; }
        .btn {
          display: inline-block;
          padding: 0.75rem 1.5rem;
          border-radius: ${borderRadius}px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.2s;
          box-shadow: ${boxShadow};
        }
        .btn-primary {
          background: ${accentColor};
          color: white;
        }
        .btn-secondary {
          background: ${textColor};
          color: white;
        }
        .btn-outline {
          border: 2px solid ${accentColor};
          color: ${accentColor};
        }
        img {
          border-radius: ${borderRadius}px;
        }
      </style>
    </head>
    <body>
      ${getTemplateHtml(formData, templateType, accentColor, headingColor)}
    </body>
    </html>
  `;

  return baseHtml;
}

function getTemplateHtml(
  formData: PageEditorInput,
  templateType: TemplateType,
  accentColor: string,
  headingColor: string
): string {
  if (templateType === 'product-launch') {
    const data = formData as ProductInput;
    const hero = data.content?.hero || {};
    const cta = data.content?.cta || {};
    const alignment = hero.heroAlignment || 'center';

    return `
      <div class="container" style="text-align: ${alignment}; padding-top: 4rem;">
        ${hero.heroImageUrl ? `<img src="${hero.heroImageUrl}" alt="Hero" style="max-width: 100%; height: auto; border-radius: 1rem; margin-bottom: 2rem;">` : ''}
        <h1 style="font-size: 3rem; font-weight: 800; margin-bottom: 1.5rem; line-height: 1.2;">
          ${hero.headline || 'Your Headline Here'}
        </h1>
        ${hero.subheadline ? `<p style="font-size: 1.25rem; margin-bottom: 2rem; opacity: 0.8;">${hero.subheadline}</p>` : ''}
        <div style="margin-top: 2.5rem;">
          <a href="${cta.buttonUrl || '#'}" class="btn btn-${cta.buttonStyle || 'primary'}">
            ${cta.buttonText || 'Get Started'}
          </a>
        </div>
      </div>
    `;
  }

  if (templateType === 'link-in-bio') {
    const data = formData as LinkInBioInput;
    const profile = data.content?.profile || {};
    const links = data.content?.links || [];

    return `
      <div class="container" style="text-align: center; padding-top: 3rem; max-width: 600px;">
        ${profile.avatarUrl ? `<img src="${profile.avatarUrl}" alt="${profile.name}" style="width: 120px; height: 120px; border-radius: 50%; border: 4px solid ${accentColor}; margin-bottom: 1.5rem;">` : ''}
        <h1 style="font-size: 2rem; font-weight: 700; margin-bottom: 0.5rem;">
          ${profile.name || 'Your Name'}
        </h1>
        ${profile.tagline ? `<p style="opacity: 0.7; margin-bottom: 2rem;">${profile.tagline}</p>` : ''}
        <div style="margin-top: 2rem; display: flex; flex-direction: column; gap: 1rem;">
          ${links
            .map(
              (link) => `
            <a href="${link.url}" style="padding: 1rem 1.5rem; background: white; border: 2px solid #e5e7eb; border-radius: 0.75rem; text-decoration: none; color: ${headingColor}; font-weight: 500; transition: all 0.2s; display: block;">
              ${link.icon ? `${link.icon} ` : ''}${link.label}
            </a>
          `
            )
            .join('')}
        </div>
      </div>
    `;
  }

  if (templateType === 'event') {
    const data = formData as EventInput;
    const hero = data.content?.hero || {};

    return `
      <div style="${hero.backgroundImage ? `background: linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${hero.backgroundImage}); background-size: cover; background-position: center;` : ''} padding: 4rem 0;">
        <div class="container" style="text-align: center; ${hero.backgroundImage ? 'color: white;' : ''}">
          <h1 style="font-size: 3.5rem; font-weight: 800; margin-bottom: 1rem; ${hero.backgroundImage ? 'color: white;' : ''}">
            ${hero.title || 'Event Title'}
          </h1>
          ${hero.subtitle ? `<p style="font-size: 1.5rem; margin-bottom: 2rem; opacity: 0.9;">${hero.subtitle}</p>` : ''}
          <div style="display: flex; justify-content: center; gap: 2rem; flex-wrap: wrap; margin-top: 2rem;">
            ${hero.date ? `<div><strong>📅 ${hero.date}</strong></div>` : ''}
            ${hero.time ? `<div><strong>🕐 ${hero.time}</strong></div>` : ''}
            ${hero.location ? `<div><strong>📍 ${hero.location}</strong></div>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  return '<div class="container"><p>Preview not available</p></div>';
}

// =============================================================================
// Component
// =============================================================================

export function PageEditorModal() {
  const { pageEditorPageId, closePageEditor, selectedTemplateType } = useUIStore();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');

  const isOpen = pageEditorPageId !== null;
  const isEditMode = typeof pageEditorPageId === 'string';
  const editingPageId = isEditMode ? pageEditorPageId : null;

  // Fetch projects for dropdown
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { data: [] };
      return response.json();
    },
    enabled: isOpen,
  });

  const projects: ProjectOption[] = projectsData?.data || [];

  // Fetch existing page data for edit mode
  const { data: existingPage, isLoading: isLoadingPage } = useQuery({
    queryKey: ['landing-page', editingPageId],
    queryFn: async () => {
      const response = await apiFetch(`/api/landing-pages/${editingPageId}`);
      if (!response.ok) throw new Error('Failed to fetch page');
      return response.json();
    },
    enabled: isEditMode && !!editingPageId,
  });

  // Determine template type - use page's actual type in edit mode, otherwise from store
  const templateType: TemplateType =
    isEditMode && existingPage?.templateType
      ? (existingPage.templateType as TemplateType)
      : (selectedTemplateType as TemplateType) || 'product-launch';

  // Get defaults and schema based on template
  const defaults = getDefaultsForTemplate(templateType);
  const schema = getSchemaForTemplate(templateType);

  const wizardSteps = getWizardSteps();

  const form = useForm<PageEditorInput>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  // Reset form when modal opens/closes or template changes
  useEffect(() => {
    if (!isOpen) {
      form.reset(defaults);
    } else if (!isEditMode) {
      form.reset(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, templateType]);

  // Load existing page data in edit mode
  useEffect(() => {
    if (existingPage && isEditMode) {
      const formData = mapExistingPageToForm(existingPage, templateType);
      form.reset(formData);
    }
  }, [existingPage, isEditMode, form, templateType]);

  // Create mutation - now includes publish and link/QR creation
  const createMutation = useMutation({
    mutationFn: async (data: PageEditorInput) => {
      const payload = buildApiPayload(data, templateType);

      const response = await apiFetch('/api/landing-pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create page');
      }
      const createdPage = await response.json();
      const pageId = createdPage.id;

      // Publish the page
      const publishResponse = await apiFetch(`/api/landing-pages/${pageId}/publish`, {
        method: 'POST',
      });
      let publicUrl = '';
      if (publishResponse.ok) {
        const publishData = await publishResponse.json();
        publicUrl = publishData.publicUrl;
      }

      // Generate short link and QR code if requested using the dedicated endpoint
      if ((data.createShortLink || data.createQRCode) && pageId) {
        try {
          await apiFetch(`/api/landing-pages/${pageId}/generate-assets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              createShortLink: data.createShortLink ?? true,
              createQRCode: data.createQRCode ?? true,
            }),
          });
        } catch {
          // Non-fatal: page was created successfully
          console.warn('Failed to generate assets for page');
        }
      }

      return { ...createdPage, isPublished: true, publicUrl };
    },
    onSuccess: (data) => {
      toast.success('Page published successfully!', {
        description: data.publicUrl,
        action: data.publicUrl
          ? {
              label: 'Open',
              onClick: () => window.open(data.publicUrl, '_blank'),
            }
          : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      closePageEditor();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create page');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: PageEditorInput) => {
      const payload = buildApiPayload(data, templateType, true);

      const response = await apiFetch(`/api/landing-pages/${editingPageId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update page');
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success('Page updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
      queryClient.invalidateQueries({ queryKey: ['landing-page', editingPageId] });
      closePageEditor();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update page');
    },
  });

  // Handle complete - called from wizard navigation
  const handleComplete = async () => {
    const data = form.getValues();

    // Validate the form
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error('Please fix the errors before publishing');
      return;
    }

    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  // Handle image upload (shared for all templates)
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, fieldPath: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiFetch('/api/uploads', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const data = await response.json();
      form.setValue(fieldPath as keyof PageEditorInput, data.url, {
        shouldDirty: true,
        shouldValidate: true,
      });
      toast.success('Image uploaded successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  // ⚡ Performance: Watch form values for preview generation
  const formValues = form.watch();
  const debouncedFormValues = useDebounce(formValues, 300);

  // Generate preview HTML when form values change
  useEffect(() => {
    if (!isOpen) return;

    const html = generatePreviewHtml(debouncedFormValues, templateType);
    setPreviewHtml(html);
  }, [debouncedFormValues, templateType, isOpen]);

  // ⚡ Performance Optimization: Use shared keyboard hook instead of manual listener
  useKeyboard('Escape', closePageEditor, isOpen);

  if (!isOpen) return null;

  // Show loading state while fetching existing page or projects in edit mode
  if (isEditMode && (isLoadingPage || isLoadingProjects)) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div className="relative z-10 w-full max-w-2xl bg-background rounded-2xl shadow-xl border p-12 flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Loading page...</p>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <SplitScreenWizardModal
        isOpen={isOpen}
        onClose={closePageEditor}
        title={isEditMode ? 'Edit Page' : `Create ${TEMPLATE_LABELS[templateType]} Page`}
        subtitle={`Build a ${TEMPLATE_LABELS[templateType].toLowerCase()} landing page`}
        steps={wizardSteps}
        initialStep={0}
        isEditMode={isEditMode}
        onComplete={handleComplete}
        showSaveDraft={false}
        completeButtonText="Publish"
        accentColor="violet"
        previewHtml={previewHtml}
        hasPreview={true}
      >
        <PageWizardContent
          templateType={templateType}
          projects={projects}
          existingPage={existingPage}
          isEditMode={isEditMode}
          isUploading={isUploading}
          onImageUpload={handleImageUpload}
          isMobile={isMobile}
        />
      </SplitScreenWizardModal>
    </FormProvider>
  );
}

// =============================================================================
// Wizard Content Component
// =============================================================================

interface PageWizardContentProps {
  templateType: TemplateType;
  projects: ProjectOption[];
  existingPage?: Record<string, unknown>;
  isEditMode: boolean;
  isUploading: boolean;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>, path: string) => void;
  isMobile: boolean;
}

function PageWizardContent({
  templateType,
  projects,
  existingPage,
  isEditMode,
  isUploading,
  onImageUpload,
  isMobile,
}: PageWizardContentProps) {
  return (
    <>
      <WizardStep stepId="basics">
        <BasicsStep projects={projects} isMobile={isMobile} />
      </WizardStep>

      <WizardStep stepId="content">
        <ContentStep
          templateType={templateType}
          isUploading={isUploading}
          onImageUpload={onImageUpload}
        />
      </WizardStep>

      <WizardStep stepId="style">
        <StyleStep />
      </WizardStep>

      <WizardStep stepId="publish">
        <PublishStep existingPage={existingPage} isEditMode={isEditMode} />
      </WizardStep>
    </>
  );
}

// =============================================================================
// Step Components
// =============================================================================

function BasicsStep({ projects, isMobile }: { projects: ProjectOption[]; isMobile: boolean }) {
  const form = useFormContext<PageEditorInput>();

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Page Name */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">
          Page Name <span className="text-destructive">*</span>
        </label>
        <input
          type="text"
          {...form.register('name')}
          className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="My Landing Page"
          autoFocus={!isMobile}
        />
        {form.formState.errors.name && (
          <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">
          Description <span className="text-muted-foreground text-xs">(optional)</span>
        </label>
        <textarea
          {...form.register('description')}
          rows={2}
          className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          placeholder="A brief description of this page"
        />
      </div>

      {/* URL Slug */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">
          URL Slug <span className="text-destructive">*</span>
        </label>
        <div className="flex items-center">
          <span className="px-3 py-2.5 rounded-l-xl border border-r-0 bg-muted text-muted-foreground text-sm">
            armenian.link/p/
          </span>
          <input
            type="text"
            {...form.register('slug')}
            className="flex-1 px-3 py-2.5 rounded-r-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            placeholder="my-landing-page"
          />
        </div>
        {form.formState.errors.slug && (
          <p className="text-sm text-destructive">{form.formState.errors.slug.message}</p>
        )}
      </div>

      {/* Project Selection */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium">
          <Layers className="h-4 w-4 text-muted-foreground" />
          Project <span className="text-destructive">*</span>
        </label>
        {projects.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No projects available. Create a project first.
          </p>
        ) : (
          <Select
            key={form.watch('projectId') || 'empty'}
            value={form.watch('projectId')}
            onValueChange={(value) => form.setValue('projectId', value)}
          >
            <SelectTrigger className="w-full max-w-xs rounded-xl">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: project.color }}
                    />
                    {project.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {form.formState.errors.projectId && (
          <p className="text-sm text-destructive">{form.formState.errors.projectId.message}</p>
        )}
      </div>
    </div>
  );
}

// Content step that routes to template-specific content
function ContentStep({
  templateType,
  isUploading,
  onImageUpload,
}: {
  templateType: TemplateType;
  isUploading: boolean;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>, path: string) => void;
}) {
  return (
    <>
      {templateType === 'product-launch' && (
        <ProductContentStep isUploading={isUploading} onImageUpload={onImageUpload} />
      )}
      {templateType === 'link-in-bio' && (
        <LinkInBioContentStep isUploading={isUploading} onImageUpload={onImageUpload} />
      )}
      {templateType === 'event' && (
        <EventContentStep isUploading={isUploading} onImageUpload={onImageUpload} />
      )}
    </>
  );
}

// =============================================================================
// Product Content Step
// =============================================================================

function ProductContentStep({
  isUploading,
  onImageUpload,
}: {
  isUploading: boolean;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>, path: string) => void;
}) {
  const form = useFormContext<ProductInput>();
  const heroImageUrl = form.watch('content.hero.heroImageUrl');

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <Type className="h-4 w-4" />
          Hero Section
        </h3>

        {/* Headline */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">
            Headline <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            {...form.register('content.hero.headline')}
            className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Your Main Headline"
          />
        </div>

        {/* Subheadline */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">
            Subheadline <span className="text-muted-foreground text-xs">(optional)</span>
          </label>
          <textarea
            {...form.register('content.hero.subheadline')}
            rows={2}
            className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            placeholder="Supporting text that elaborates on your headline"
          />
        </div>

        {/* Hero Image Upload */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Image className="h-4 w-4 text-muted-foreground" />
            Hero Image <span className="text-muted-foreground text-xs">(optional)</span>
          </label>

          {heroImageUrl ? (
            <div className="relative rounded-xl border overflow-hidden">
              <img src={heroImageUrl} alt="Hero preview" className="w-full h-40 object-cover" />
              <button
                type="button"
                onClick={() => form.setValue('content.hero.heroImageUrl', '')}
                className="absolute top-2 right-2 p-2 bg-destructive/90 hover:bg-destructive text-white rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors">
              <div className="flex flex-col items-center justify-center py-6">
                {isUploading ? (
                  <>
                    <Loader2 className="h-8 w-8 text-muted-foreground animate-spin mb-2" />
                    <p className="text-sm text-muted-foreground">Uploading…</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm font-medium">Click to upload image</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPEG, PNG, GIF, WebP (max 5MB)
                    </p>
                  </>
                )}
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={(e) => onImageUpload(e, 'content.hero.heroImageUrl')}
                disabled={isUploading}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Hero Alignment */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">Text Alignment</label>
          <div className="flex gap-2">
            {[
              { value: 'left', icon: AlignLeft, label: 'Left' },
              { value: 'center', icon: AlignCenter, label: 'Center' },
              { value: 'right', icon: AlignRight, label: 'Right' },
            ].map((alignment) => (
              <button
                key={alignment.value}
                type="button"
                onClick={() =>
                  form.setValue(
                    'content.hero.heroAlignment',
                    alignment.value as 'left' | 'center' | 'right'
                  )
                }
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
                  form.watch('content.hero.heroAlignment') === alignment.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50'
                )}
              >
                <alignment.icon className="h-4 w-4" />
                {alignment.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <MousePointerClick className="h-4 w-4" />
          Call to Action
        </h3>

        {/* Button Text */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">
            Button Text <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            {...form.register('content.cta.buttonText')}
            className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Get Started"
          />
        </div>

        {/* Button URL */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">Button Link</label>
          <input
            type="text"
            {...form.register('content.cta.buttonUrl')}
            className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="https://example.com/signup"
          />
        </div>

        {/* Button Style */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">Button Style</label>
          <div className="flex gap-2">
            {[
              { value: 'primary', label: 'Primary' },
              { value: 'secondary', label: 'Secondary' },
              { value: 'outline', label: 'Outline' },
            ].map((style) => (
              <button
                key={style.value}
                type="button"
                onClick={() =>
                  form.setValue(
                    'content.cta.buttonStyle',
                    style.value as 'primary' | 'secondary' | 'outline'
                  )
                }
                className={cn(
                  'flex-1 px-4 py-2 rounded-lg border transition-colors text-sm',
                  form.watch('content.cta.buttonStyle') === style.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50'
                )}
              >
                {style.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Form Embed Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Form Embed
        </h3>
        <FormSectionEditor
          value={form.watch('content.formSection') as FormSectionValue | null}
          onChange={(value) => form.setValue('content.formSection', value)}
          projectId={form.watch('projectId')}
        />
      </div>
    </div>
  );
}

// =============================================================================
// Link-in-Bio Content Step
// =============================================================================

function LinkInBioContentStep({
  isUploading,
  onImageUpload,
}: {
  isUploading: boolean;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>, path: string) => void;
}) {
  const form = useFormContext<LinkInBioInput>();
  const avatarUrl = form.watch('content.profile.avatarUrl');
  const links = form.watch('content.links') || [];
  const socials = form.watch('content.socials') || [];

  return (
    <div className="space-y-6">
      {/* Profile Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <Users className="h-4 w-4" />
          Profile
        </h3>

        {/* Avatar */}
        <div className="flex items-start gap-4">
          <div className="relative">
            {avatarUrl ? (
              <div className="relative">
                <img
                  src={avatarUrl}
                  alt="Avatar"
                  className="w-20 h-20 rounded-full object-cover border-2 border-primary"
                />
                <button
                  type="button"
                  onClick={() => form.setValue('content.profile.avatarUrl', '')}
                  className="absolute -top-1 -right-1 p-1 bg-destructive text-white rounded-full"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <label className="w-20 h-20 rounded-full border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-primary transition-colors">
                {isUploading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                ) : (
                  <Upload className="h-6 w-6 text-muted-foreground" />
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => onImageUpload(e, 'content.profile.avatarUrl')}
                  disabled={isUploading}
                  className="hidden"
                />
              </label>
            )}
          </div>

          <div className="flex-1 space-y-3">
            {/* Name */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                {...form.register('content.profile.name')}
                className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Your Name"
              />
            </div>

            {/* Tagline */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Tagline <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <input
                type="text"
                {...form.register('content.profile.tagline')}
                className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Developer, Creator, Maker"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Links Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Links
        </h3>

        <LinksEditor
          value={links}
          onChange={(newLinks) => form.setValue('content.links', newLinks as LinkItem[])}
          maxItems={20}
        />
      </div>

      {/* Social Links Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <Globe className="h-4 w-4" />
          Social Links
        </h3>

        <SocialsEditor
          value={socials}
          onChange={(newSocials) => form.setValue('content.socials', newSocials as SocialItem[])}
        />
      </div>

      {/* Form Embed Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Form Embed
        </h3>
        <FormSectionEditor
          value={form.watch('content.formSection') as FormSectionValue | null}
          onChange={(value) => form.setValue('content.formSection', value)}
          projectId={form.watch('projectId')}
        />
      </div>
    </div>
  );
}

// =============================================================================
// Event Content Step
// =============================================================================

function EventContentStep({
  isUploading,
  onImageUpload,
}: {
  isUploading: boolean;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>, path: string) => void;
}) {
  const form = useFormContext<EventInput>();
  const backgroundImage = form.watch('content.hero.backgroundImage');
  const gallery = form.watch('content.gallery') || [];
  const schedule = form.watch('content.schedule') || [];
  const faq = form.watch('content.faq') || [];

  return (
    <div className="space-y-6">
      {/* Event Details */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Event Details
        </h3>

        {/* Title */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">
            Event Title <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            {...form.register('content.hero.title')}
            className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Summer Garden Party"
          />
        </div>

        {/* Subtitle */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">
            Subtitle <span className="text-muted-foreground text-xs">(optional)</span>
          </label>
          <input
            type="text"
            {...form.register('content.hero.subtitle')}
            className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Join us for an afternoon of fun!"
          />
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              Date
            </label>
            <input
              type="text"
              {...form.register('content.hero.date')}
              className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Saturday, July 15th"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Time
            </label>
            <input
              type="text"
              {...form.register('content.hero.time')}
              className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="2:00 PM - 8:00 PM"
            />
          </div>
        </div>

        {/* Location */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            Location
          </label>
          <input
            type="text"
            {...form.register('content.hero.location')}
            className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="123 Main Street, City"
          />
        </div>

        {/* Location URL */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">
            Maps Link <span className="text-muted-foreground text-xs">(optional)</span>
          </label>
          <input
            type="url"
            {...form.register('content.hero.locationUrl')}
            className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            placeholder="https://maps.google.com/…"
          />
        </div>

        {/* Background Image */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            Background Image <span className="text-muted-foreground text-xs">(optional)</span>
          </label>

          {backgroundImage ? (
            <div className="relative rounded-xl border overflow-hidden">
              <img
                src={backgroundImage}
                alt="Background preview"
                className="w-full h-32 object-cover"
              />
              <button
                type="button"
                onClick={() => form.setValue('content.hero.backgroundImage', '')}
                className="absolute top-2 right-2 p-2 bg-destructive/90 hover:bg-destructive text-white rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors">
              {isUploading ? (
                <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
              ) : (
                <>
                  <Upload className="h-6 w-6 text-muted-foreground mb-1" />
                  <span className="text-sm text-muted-foreground">Upload background image</span>
                </>
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={(e) => onImageUpload(e, 'content.hero.backgroundImage')}
                disabled={isUploading}
                className="hidden"
              />
            </label>
          )}
        </div>
      </div>

      {/* Gallery Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <ImageIcon className="h-4 w-4" />
          Photo Gallery
        </h3>

        <GalleryEditor
          value={gallery}
          onChange={(newGallery) => form.setValue('content.gallery', newGallery as GalleryImage[])}
          maxItems={8}
        />
      </div>

      {/* Schedule Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Schedule
        </h3>

        <ScheduleEditor
          value={schedule}
          onChange={(newSchedule) =>
            form.setValue('content.schedule', newSchedule as ScheduleItem[])
          }
          maxItems={10}
        />
      </div>

      {/* FAQ Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <HelpCircle className="h-4 w-4" />
          FAQ
        </h3>

        <FAQEditor
          value={faq}
          onChange={(newFaq) => form.setValue('content.faq', newFaq as FAQItem[])}
          maxItems={10}
        />
      </div>

      {/* RSVP Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <MousePointerClick className="h-4 w-4" />
          RSVP Button
        </h3>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.watch('content.rsvp.enabled') || false}
              onChange={(e) => form.setValue('content.rsvp.enabled', e.target.checked)}
              className="w-5 h-5 rounded border-2 border-primary text-primary focus:ring-primary focus:ring-offset-0"
            />
            <span className="font-medium">Show RSVP button</span>
          </label>

          {form.watch('content.rsvp.enabled') && (
            <div className="space-y-3 pl-8">
              <div className="space-y-2">
                <label className="block text-sm font-medium">Button Text</label>
                <input
                  type="text"
                  {...form.register('content.rsvp.buttonText')}
                  className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="RSVP Now"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium">Button Link</label>
                <input
                  type="url"
                  {...form.register('content.rsvp.buttonUrl')}
                  className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="https://forms.example.com/rsvp"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Embed Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Form Embed
        </h3>
        <FormSectionEditor
          value={form.watch('content.formSection') as FormSectionValue | null}
          onChange={(value) => form.setValue('content.formSection', value)}
          projectId={form.watch('projectId')}
        />
      </div>
    </div>
  );
}

// =============================================================================
// Style Step (shared by all templates)
// =============================================================================

function StyleStep() {
  const form = useFormContext<PageEditorInput>();
  const styleOptions = form.watch('styleOptions');

  const updateStyle = (key: keyof typeof styleOptions, value: string) => {
    form.setValue(`styleOptions.${key}` as 'styleOptions.backgroundColor', value);
  };

  const handleBackgroundColorChange = (color: string) => {
    updateStyle('backgroundColor', color);
    // Auto-adjust text colors for dark backgrounds
    const isDark = ['#1f2937', '#111827', '#000000'].includes(color);
    if (isDark) {
      updateStyle('textColor', '#f9fafb');
      updateStyle('headingColor', '#ffffff');
    } else if (styleOptions?.textColor === '#f9fafb' || styleOptions?.textColor === '#ffffff') {
      updateStyle('textColor', '#1f2937');
      updateStyle('headingColor', '#111827');
    }
  };

  const handleApplyTheme = (theme: PageTheme) => {
    // Apply all theme settings at once
    form.setValue('styleOptions.backgroundColor', theme.colors.backgroundColor);
    form.setValue('styleOptions.accentColor', theme.colors.accentColor);
    form.setValue('styleOptions.textColor', theme.colors.textColor);
    form.setValue('styleOptions.headingColor', theme.colors.headingColor);
    form.setValue('styleOptions.fontFamily', theme.typography.fontFamily);
    form.setValue('styleOptions.maxWidth', theme.layout.maxWidth);
    form.setValue('styleOptions.padding', theme.layout.padding);
  };

  return (
    <div className="space-y-8 max-w-2xl">
      {/* Theme Picker Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">Choose a Theme</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Start with a pre-built theme or create your own
        </p>
        <div className="p-4 border rounded-xl bg-muted/20">
          <ThemePicker
            currentTheme={{
              backgroundColor: styleOptions?.backgroundColor || '#ffffff',
              accentColor: styleOptions?.accentColor || '#10b981',
              textColor: styleOptions?.textColor || '#1f2937',
              headingColor: styleOptions?.headingColor || '#111827',
              fontFamily: styleOptions?.fontFamily || 'inter',
              maxWidth: styleOptions?.maxWidth || 'medium',
              padding: styleOptions?.padding || 'normal',
            }}
            onApplyTheme={handleApplyTheme}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or customize manually</span>
        </div>
      </div>

      {/* Manual Color Customization */}
      <div className="space-y-6">
        {/* Accent Color */}
        <div className="p-4 border rounded-xl bg-muted/20">
          <ColorPicker
            value={styleOptions?.accentColor || '#10b981'}
            onChange={(color) => updateStyle('accentColor', color)}
            label="Accent Color"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Used for buttons, links, and highlights
          </p>
        </div>

        {/* Background Color */}
        <div className="p-4 border rounded-xl bg-muted/20">
          <ColorPicker
            value={styleOptions?.backgroundColor || '#ffffff'}
            onChange={handleBackgroundColorChange}
            label="Background Color"
          />
          <p className="text-xs text-muted-foreground mt-2">Main background color for your page</p>
        </div>
      </div>

      {/* Typography Section */}
      <div className="pt-4 border-t">
        <div className="p-4 border rounded-xl bg-muted/20">
          <TypographyControls
            value={{
              fontFamily: styleOptions?.fontFamily || 'inter',
              headingSize: styleOptions?.headingSize,
              bodySize: styleOptions?.bodySize,
              lineHeight: styleOptions?.lineHeight,
              letterSpacing: styleOptions?.letterSpacing,
              headingWeight: styleOptions?.headingWeight,
              bodyWeight: styleOptions?.bodyWeight,
            }}
            onChange={(settings) => {
              if (settings.fontFamily)
                form.setValue('styleOptions.fontFamily', settings.fontFamily);
              if (settings.headingSize !== undefined)
                form.setValue('styleOptions.headingSize', settings.headingSize);
              if (settings.bodySize !== undefined)
                form.setValue('styleOptions.bodySize', settings.bodySize);
              if (settings.lineHeight !== undefined)
                form.setValue('styleOptions.lineHeight', settings.lineHeight);
              if (settings.letterSpacing !== undefined)
                form.setValue('styleOptions.letterSpacing', settings.letterSpacing);
              if (settings.headingWeight !== undefined)
                form.setValue('styleOptions.headingWeight', settings.headingWeight);
              if (settings.bodyWeight !== undefined)
                form.setValue('styleOptions.bodyWeight', settings.bodyWeight);
            }}
          />
        </div>
      </div>

      {/* Visual Effects Section */}
      <div className="pt-4 border-t">
        <div className="p-4 border rounded-xl bg-muted/20">
          <VisualEffectsControls
            value={{
              borderRadius: styleOptions?.borderRadius,
              shadowX: styleOptions?.shadowX,
              shadowY: styleOptions?.shadowY,
              shadowBlur: styleOptions?.shadowBlur,
              shadowSpread: styleOptions?.shadowSpread,
              shadowColor: styleOptions?.shadowColor,
              shadowEnabled: styleOptions?.shadowEnabled,
              gradientEnabled: styleOptions?.gradientEnabled,
              gradientType: styleOptions?.gradientType,
              gradientAngle: styleOptions?.gradientAngle,
              gradientStops: styleOptions?.gradientStops,
              opacity: styleOptions?.opacity,
            }}
            onChange={(settings) => {
              if (settings.borderRadius !== undefined)
                form.setValue('styleOptions.borderRadius', settings.borderRadius);
              if (settings.shadowX !== undefined)
                form.setValue('styleOptions.shadowX', settings.shadowX);
              if (settings.shadowY !== undefined)
                form.setValue('styleOptions.shadowY', settings.shadowY);
              if (settings.shadowBlur !== undefined)
                form.setValue('styleOptions.shadowBlur', settings.shadowBlur);
              if (settings.shadowSpread !== undefined)
                form.setValue('styleOptions.shadowSpread', settings.shadowSpread);
              if (settings.shadowColor !== undefined)
                form.setValue('styleOptions.shadowColor', settings.shadowColor);
              if (settings.shadowEnabled !== undefined)
                form.setValue('styleOptions.shadowEnabled', settings.shadowEnabled);
              if (settings.gradientEnabled !== undefined)
                form.setValue('styleOptions.gradientEnabled', settings.gradientEnabled);
              if (settings.gradientType !== undefined)
                form.setValue('styleOptions.gradientType', settings.gradientType);
              if (settings.gradientAngle !== undefined)
                form.setValue('styleOptions.gradientAngle', settings.gradientAngle);
              if (settings.gradientStops !== undefined)
                form.setValue('styleOptions.gradientStops', settings.gradientStops);
              if (settings.opacity !== undefined)
                form.setValue('styleOptions.opacity', settings.opacity);
            }}
          />
        </div>
      </div>

      {/* Layout Section */}
      <div className="pt-4 border-t space-y-4">
        {/* Max Width */}
        <div>
          <label className="block text-sm font-medium mb-3">Content Width</label>
          <div className="flex gap-2">
            {[
              { value: 'narrow', label: 'Narrow' },
              { value: 'medium', label: 'Medium' },
              { value: 'wide', label: 'Wide' },
            ].map((width) => (
              <button
                key={width.value}
                type="button"
                onClick={() =>
                  form.setValue(
                    'styleOptions.maxWidth',
                    width.value as 'narrow' | 'medium' | 'wide'
                  )
                }
                className={cn(
                  'flex-1 px-4 py-2 rounded-lg border transition-colors text-sm',
                  styleOptions?.maxWidth === width.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50'
                )}
              >
                {width.label}
              </button>
            ))}
          </div>
        </div>

        {/* Padding */}
        <div>
          <label className="block text-sm font-medium mb-3">Spacing</label>
          <div className="flex gap-2">
            {[
              { value: 'compact', label: 'Compact' },
              { value: 'normal', label: 'Normal' },
              { value: 'spacious', label: 'Spacious' },
            ].map((padding) => (
              <button
                key={padding.value}
                type="button"
                onClick={() =>
                  form.setValue(
                    'styleOptions.padding',
                    padding.value as 'compact' | 'normal' | 'spacious'
                  )
                }
                className={cn(
                  'flex-1 px-4 py-2 rounded-lg border transition-colors text-sm',
                  styleOptions?.padding === padding.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50'
                )}
              >
                {padding.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Publish Step (includes preview and links options)
// =============================================================================

interface PublishStepProps {
  existingPage?: Record<string, unknown>;
  isEditMode: boolean;
}

function PublishStep({ existingPage, isEditMode }: PublishStepProps) {
  const form = useFormContext<PageEditorInput>();
  const [deviceMode, setDeviceMode] = useState<'desktop' | 'mobile'>('desktop');
  const formData = form.getValues();
  const createShortLink = form.watch('createShortLink');
  const createQRCode = form.watch('createQRCode');

  // Generate preview URL - for existing pages, use their public URL
  const previewUrl =
    existingPage?.isPublished && existingPage?.publicUrl
      ? (existingPage.publicUrl as string)
      : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Column - Preview */}
      <div className="space-y-4">
        {/* Device toggle */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Preview</h3>
          <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
            <Button
              type="button"
              variant={deviceMode === 'desktop' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setDeviceMode('desktop')}
              className="gap-1.5 h-7 px-2 text-xs"
            >
              <Monitor className="h-3.5 w-3.5" />
              Desktop
            </Button>
            <Button
              type="button"
              variant={deviceMode === 'mobile' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setDeviceMode('mobile')}
              className="gap-1.5 h-7 px-2 text-xs"
            >
              <Smartphone className="h-3.5 w-3.5" />
              Mobile
            </Button>
          </div>
        </div>

        {/* Preview area */}
        <div
          className={cn(
            'bg-muted/30 rounded-xl border overflow-hidden transition-all mx-auto',
            deviceMode === 'mobile' ? 'max-w-[320px] h-[480px]' : 'w-full h-[400px]'
          )}
        >
          {previewUrl ? (
            <iframe src={previewUrl} className="w-full h-full border-0" title="Page Preview" />
          ) : existingPage?.html ? (
            <iframe
              srcDoc={existingPage.html as string}
              className="w-full h-full border-0"
              title="Page Preview"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-muted/50 to-muted/20">
              <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg bg-gradient-to-br from-violet-500 to-purple-600">
                <Layers className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-lg font-semibold mb-1">{formData.name || 'Untitled Page'}</h4>
              <p className="text-sm text-muted-foreground mb-3">
                armenian.link/p/{formData.slug || 'your-slug'}
              </p>
              {formData.description && (
                <p className="text-xs text-muted-foreground max-w-[250px] line-clamp-2">
                  {formData.description}
                </p>
              )}
              <p className="text-xs text-muted-foreground/70 mt-4 italic">
                Live preview available after first publish
              </p>
            </div>
          )}
        </div>

        {previewUrl && (
          <div className="flex justify-center">
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-primary hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open in new tab
            </a>
          </div>
        )}
      </div>

      {/* Right Column - Summary & Options */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium mb-1">
            {isEditMode ? 'Save Changes' : 'Ready to Publish'}
          </h3>
          <p className="text-xs text-muted-foreground">
            {isEditMode ? 'Review and save your changes.' : 'Your page is ready to go live.'}
          </p>
        </div>

        {/* Existing page status */}
        {isEditMode && existingPage && (
          <div className="flex items-center gap-2 text-sm">
            <span
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                existingPage.isPublished
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                  : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
              )}
            >
              {existingPage.isPublished ? 'Published' : 'Draft'}
            </span>
          </div>
        )}

        {/* Links & QR Options (only for new pages) */}
        {!isEditMode && (
          <div className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Sharing Options
            </h4>

            {/* Short Link Option */}
            <label className="flex items-center gap-3 p-3 rounded-lg border bg-card cursor-pointer hover:border-primary/50 transition-colors">
              <div className="h-8 w-8 rounded-lg bg-blue-100 dark:bg-blue-950 flex items-center justify-center flex-shrink-0">
                <Link2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium">Create Short Link</span>
                <p className="text-xs text-muted-foreground">Trackable URL for sharing</p>
              </div>
              <input
                type="checkbox"
                checked={createShortLink ?? true}
                onChange={(e) => {
                  form.setValue('createShortLink', e.target.checked);
                  if (!e.target.checked) {
                    form.setValue('createQRCode', false);
                  }
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
            </label>

            {/* QR Code Option */}
            <label
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border bg-card cursor-pointer hover:border-primary/50 transition-colors',
                !createShortLink && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="h-8 w-8 rounded-lg bg-purple-100 dark:bg-purple-950 flex items-center justify-center flex-shrink-0">
                <QrCode className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium">Generate QR Code</span>
                <p className="text-xs text-muted-foreground">
                  {createShortLink ? 'Scannable code for print' : 'Requires short link'}
                </p>
              </div>
              <input
                type="checkbox"
                checked={createQRCode ?? true}
                disabled={!createShortLink}
                onChange={(e) => form.setValue('createQRCode', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 disabled:opacity-50"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Helper Functions
// =============================================================================

function mapExistingPageToForm(
  page: Record<string, unknown>,
  templateType: TemplateType
): PageEditorInput {
  const base = {
    name: (page.name as string) || '',
    description: (page.description as string) || '',
    slug: (page.slug as string) || '',
    projectId: (page.projectId as string) || '',
    styleOptions:
      (page.styleOptions as PageEditorInput['styleOptions']) ||
      getDefaultsForTemplate(templateType).styleOptions,
  };

  const content = (page.content as Record<string, unknown>) || {};

  switch (templateType) {
    case 'link-in-bio':
      return {
        ...base,
        content: {
          profile:
            (content.profile as LinkInBioInput['content']['profile']) ||
            linkInBioDefaults.content.profile,
          links: (content.links as LinkInBioInput['content']['links']) || [],
          socials: (content.socials as LinkInBioInput['content']['socials']) || [],
        },
      } as LinkInBioInput;

    case 'event':
      return {
        ...base,
        content: {
          hero: (content.hero as EventInput['content']['hero']) || eventDefaults.content.hero,
          gallery: (content.gallery as EventInput['content']['gallery']) || [],
          schedule: (content.schedule as EventInput['content']['schedule']) || [],
          faq: (content.faq as EventInput['content']['faq']) || [],
          rsvp: (content.rsvp as EventInput['content']['rsvp']) || eventDefaults.content.rsvp,
        },
      } as EventInput;

    default:
      return {
        ...base,
        content: {
          hero: (content.hero as ProductInput['content']['hero']) || productDefaults.content.hero,
          cta: (content.cta as ProductInput['content']['cta']) || productDefaults.content.cta,
        },
      } as ProductInput;
  }
}

function buildApiPayload(data: PageEditorInput, templateType: TemplateType, isUpdate = false) {
  const base = {
    slug: data.slug,
    name: data.name,
    description: data.description || (isUpdate ? null : undefined),
    styleOptions: data.styleOptions,
  };

  if (!isUpdate) {
    return {
      ...base,
      projectId: data.projectId,
      templateType,
      content: data.content,
    };
  }

  return {
    ...base,
    content: data.content,
  };
}
